import {useCallback, useEffect, useState} from 'react';
import block from 'bem-cn-lite';
import {Button, Icon, Text} from '@gravity-ui/uikit';
import {getCookie, setCookie} from '@utils';
import CookiesIcon from '@assets/cookies-icon.svg';
import {Xmark} from '@gravity-ui/icons';

import './CookieConsentToastGate.scss';

const b = block('cookie-consent');

const CONSENT_COOKIE = 'cookie_consent';
const ONE_YEAR = 60 * 60 * 24 * 365;
const ANIMATION_MS = 180;

type ConsentValue = 'accepted' | 'declined';

let dismissedUntilReload = false;

export const CookieConsentToastGate: React.FC = () => {
    const [mounted, setMounted] = useState(false);
    const [open, setOpen] = useState(false);

    const hasConsent = () => {
        const consent = getCookie(CONSENT_COOKIE);
        return consent === 'accepted' || consent === 'declined';
    };

    const show = useCallback(() => {
        setMounted(true);
        requestAnimationFrame(() => setOpen(true));
    }, []);

    const hide = useCallback(() => {
        setOpen(false);
        window.setTimeout(() => setMounted(false), ANIMATION_MS);
    }, []);

    const saveConsent = useCallback(
        (value: ConsentValue) => {
            setCookie(CONSENT_COOKIE, value, {
                maxAgeSeconds: ONE_YEAR,
                path: '/',
                sameSite: 'Lax',
                secure: window.location.protocol === 'https:'
            });

            hide();
        },
        [hide]
    );

    const onCloseClick = useCallback(() => {
        dismissedUntilReload = true; // не показываем до reload
        hide();
    }, [hide]);

    useEffect(() => {
        if (hasConsent()) return;
        if (dismissedUntilReload) return;
        show();
    }, [show]);

    if (!mounted) return null;

    return (
        <div className={b('popup', {open})} role="dialog" aria-live="polite">
            <img src={CookiesIcon} alt="cookies" />

            <div className={b('content')}>
                <div className={b('text')}>
                    <Text variant="subheader-2">Мы используем Cookies</Text>
                    <Text variant="body-1">
                        Для улучшения вашего опыта на сайте. <br />{' '}
                        <a
                            href="https://disk.yandex.ru/i/GwmTXHnpkl9zxA"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={b('link-policy')}
                        >
                            <Text variant="body-1">Политика конфиденциальности</Text>
                        </a>
                    </Text>
                </div>

                <div className={b('actions')}>
                    <Button
                        size="m"
                        onClick={() => saveConsent('accepted')}
                        className={b('accept-button')}
                    >
                        Понятно
                    </Button>

                    <Button
                        size="m"
                        view="outlined"
                        onClick={() => saveConsent('declined')}
                        className={b('decline-button')}
                    >
                        Отказаться
                    </Button>
                </div>
            </div>

            <Button size="s" view="flat" className={b('close')} onClick={onCloseClick}>
                <Icon data={Xmark} />
            </Button>
        </div>
    );
};
